import {Check, ExternalLink, Trash2} from "lucide-react";
import type {KeyboardEvent} from "react";

import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {Card, CardContent} from "@/components/ui/card";
import {Checkbox} from "@/components/ui/checkbox";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {cn} from "@/lib/utils";
import {formatRelativeTime} from "@/src/modules/core/lib/formatters";

import {getNotificationSubjectPath} from "../lib/get-notification-subject-path";
import {isTopicAckedBySubjectOpen} from "../lib/notification-feed-topics";
import {
  getNotificationCardDisplay,
  getNotificationStatusBadge,
} from "../lib/notification-display";
import type {NotificationItem} from "../types";

const cardActionBtn =
  "h-9 w-9 min-h-9 min-w-9 shrink-0 [&_svg]:!size-3.5";

const TOPIC_LABELS: Record<string, string> = {
  stored_vehicle: "Stored vehicles",
  briefing_board: "Briefing boards",
  direct_message: "Direct messages",
  shift: "Shift",
  household: "Household",
};

function getTopicLabel(topic: string) {
  return TOPIC_LABELS[topic] ?? topic.replaceAll("_", " ");
}

function getAccessibleLabel(item: NotificationItem): string {
  const {headline, subline, body} = getNotificationCardDisplay(item);
  return [headline, subline, body].filter(Boolean).join(" — ");
}

type NotificationFeedRowProps = {
  item: NotificationItem;
  isSelected?: boolean;
  withSelection?: boolean;
  withTopic?: boolean;
  isDismissPending?: boolean;
  isMarkReadPending?: boolean;
  onSelectionChange?: (selected: boolean) => void;
  onMarkRead?: (item: NotificationItem) => void;
  onDismiss?: (id: string) => void;
  onOpenSubject?: (item: NotificationItem, path: string) => void;
};

export function NotificationFeedRow({
  item,
  isSelected = false,
  withSelection = false,
  withTopic = false,
  isDismissPending = false,
  isMarkReadPending = false,
  onSelectionChange,
  onMarkRead,
  onDismiss,
  onOpenSubject,
}: NotificationFeedRowProps) {
  const isUnread = !item.read_at && !item.discarded_at;
  const {headline, subline, body} = getNotificationCardDisplay(item);
  const accessibleLabel = getAccessibleLabel(item);
  const statusBadge = getNotificationStatusBadge(item);
  const subjectPath = getNotificationSubjectPath(item);
  const canOpenSubject = subjectPath !== "/notifications";
  const suppressMarkRead = isTopicAckedBySubjectOpen(item.topic);

  const handleOpenSubject = () => {
    if (canOpenSubject) {
      onOpenSubject?.(item, subjectPath);
    }
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!canOpenSubject) {
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleOpenSubject();
    }
  };

  return (
    <div className="flex items-start gap-3">
      {withSelection ? (
        <Checkbox
          className="mt-3"
          checked={isSelected}
          aria-label={`Select notification ${accessibleLabel}`}
          onCheckedChange={(value) => onSelectionChange?.(value === true)}
        />
      ) : null}
      <Card className={cn("flex-1 gap-0 py-0", isUnread && "border-primary/40")}>
        <div
          role={canOpenSubject ? "button" : undefined}
          tabIndex={canOpenSubject ? 0 : undefined}
          aria-label={canOpenSubject ? `Open: ${accessibleLabel}` : undefined}
          className={cn(
            "flex flex-col",
            canOpenSubject && "cursor-pointer rounded-xl transition-colors hover:bg-accent/40",
          )}
          onClick={canOpenSubject ? handleOpenSubject : undefined}
          onKeyDown={handleCardKeyDown}
        >
          <CardContent className="flex flex-col gap-1.5 px-4 pt-3 pb-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                <p className="text-foreground text-sm leading-snug font-semibold">{headline}</p>
                {subline ? (
                  <p className="text-muted-foreground text-sm font-medium leading-snug wrap-break-word">
                    {subline}
                  </p>
                ) : null}
              </div>
              {(statusBadge || withTopic) ? (
                <div className="flex shrink-0 flex-col items-end gap-1">
                  {statusBadge ? (
                    <Badge variant={statusBadge.variant} className="px-1.5 py-0 text-[10px]">
                      {statusBadge.label}
                    </Badge>
                  ) : null}
                  {withTopic ? (
                    <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                      {getTopicLabel(item.topic)}
                    </Badge>
                  ) : null}
                </div>
              ) : null}
            </div>
            {body ? (
              <p className="text-muted-foreground line-clamp-2 text-xs leading-relaxed">{body}</p>
            ) : null}
          </CardContent>
          <div className="border-border flex items-center justify-between gap-2 border-t px-4 py-2">
            <span className="text-muted-foreground min-w-0 truncate text-xs font-medium">
              {formatRelativeTime(item.created_at)}
            </span>
            <div className="flex shrink-0 items-center gap-0.5">
              {canOpenSubject && !suppressMarkRead ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className={cn(cardActionBtn, "text-primary hover:text-primary/90 hover:bg-primary/10")}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleOpenSubject();
                        }}
                      >
                        <ExternalLink />
                        <span className="sr-only">Open</span>
                      </Button>
                    }
                  />
                  <TooltipContent>Open</TooltipContent>
                </Tooltip>
              ) : null}
              {!suppressMarkRead ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className={cn(cardActionBtn, "text-primary hover:text-primary/90 hover:bg-primary/10")}
                        disabled={!isUnread || isMarkReadPending}
                        onClick={(event) => {
                          event.stopPropagation();
                          onMarkRead?.(item);
                        }}
                      >
                        <Check />
                        <span className="sr-only">Mark read</span>
                      </Button>
                    }
                  />
                  <TooltipContent>Mark read</TooltipContent>
                </Tooltip>
              ) : null}
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className={cn(
                        cardActionBtn,
                        "text-destructive hover:text-destructive/90 hover:bg-destructive/10",
                      )}
                      disabled={isDismissPending}
                      onClick={(event) => {
                        event.stopPropagation();
                        onDismiss?.(item.id);
                      }}
                    >
                      <Trash2 />
                      <span className="sr-only">Dismiss</span>
                    </Button>
                  }
                />
                <TooltipContent>Dismiss</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
